import PremiumStar from '@/components/Icons/PremiumStar';
import SteppedDrawer from '@/components/SteppedDrawer';
import { Button } from '@music-vine/cadence/ui';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@music-vine/cadence/ui';
import { cn } from '@/lib/utils';
import type { AssetGroup } from '@/types/asset';
import type { Dialog as DialogPrimitive } from 'radix-ui';
import { cva } from 'class-variance-authority';
import Image from 'next/image';
import Link from 'next/link';
import { useMemo } from 'react';
import { getImageData } from '../utils/getImageData';
import { UpdatePaymentButton } from '@/components/UpdatePaymentButton';
export interface OverduePaymentDownloadPopupProps {
  asset: AssetGroup;
  children?: React.ReactNode;
  className?: string;
  dialog?: boolean;
  drawer?: boolean;
  isPremiumAsset?: boolean;
  onOpenChange: DialogPrimitive.DialogProps['onOpenChange'];
  open: DialogPrimitive.DialogProps['open'];
}
const imageContainerVariants = cva('relative', {
  variants: {
    assetType: {
      music: 'ml-2 h-28 min-w-28',
      sfx: 'ml-2 h-28 min-w-28',
      motion: 'h-36 w-full xs:basis-2/5',
      stock_footage: 'h-36 w-full xs:basis-2/5'
    }
  }
});
const imageVariants = cva('object-cover', {
  variants: {
    assetType: {
      music: 'rounded-full',
      sfx: 'rounded-full',
      motion: 'rounded-xl',
      stock_footage: 'rounded-xl'
    }
  }
});
const premiumStarVariants = cva('absolute size-[1.125rem]', {
  variants: {
    isPremiumAsset: {
      true: 'block',
      false: 'hidden'
    },
    assetType: {
      music: 'top-2 left-2',
      sfx: 'top-2 left-2',
      motion: 'top-2 right-2',
      stock_footage: 'top-2 right-2'
    }
  },
  defaultVariants: {
    isPremiumAsset: false,
    assetType: 'music'
  }
});
const descriptionVariants = cva('', {
  variants: {
    assetType: {
      music: 'xs:basis-4/5',
      sfx: 'xs:basis-4/5',
      motion: 'xs:basis-3/5',
      stock_footage: 'xs:basis-3/5'
    }
  }
});
interface WrapperProps extends DialogPrimitive.DialogProps {
  children: React.ReactNode;
  dialog?: boolean;
  drawer?: boolean;
}
const Wrapper = ({
  dialog = true,
  drawer = true,
  children,
  open,
  onOpenChange,
  ...props
}: WrapperProps) => {
  if (!dialog) {
    return <>{children}</>;
  }
  if (drawer) {
    return <SteppedDrawer.Root onOpenChange={onOpenChange} open={open} {...props}>
        <SteppedDrawer.Content>{children}</SteppedDrawer.Content>
      </SteppedDrawer.Root>;
  }
  return <Dialog onOpenChange={onOpenChange} open={open} {...props} data-sentry-element="Dialog" data-sentry-component="Wrapper" data-sentry-source-file="PaymentIssueDownloadDialog.tsx">
      <DialogTitle className="sr-only" data-sentry-element="DialogTitle" data-sentry-source-file="PaymentIssueDownloadDialog.tsx">
        Resolve payment issue download popup
      </DialogTitle>
      <DialogDescription className="sr-only" data-sentry-element="DialogDescription" data-sentry-source-file="PaymentIssueDownloadDialog.tsx">
        Resolve payment issue to continue
      </DialogDescription>
      <DialogContent className="w-full max-w-xl pt-8 pb-6 sm:rounded-2xl" onOpenAutoFocus={e => e.preventDefault()} data-sentry-element="DialogContent" data-sentry-source-file="PaymentIssueDownloadDialog.tsx">
        {children}
      </DialogContent>
    </Dialog>;
};
export const PaymentIssueDownloadDialog = ({
  asset,
  isPremiumAsset,
  className,
  open,
  onOpenChange,
  dialog,
  drawer
}: OverduePaymentDownloadPopupProps) => {
  const data = useMemo(() => getImageData(asset), [asset]);
  return <Wrapper dialog={dialog} drawer={drawer} onOpenChange={onOpenChange} open={open} data-sentry-element="Wrapper" data-sentry-component="PaymentIssueDownloadDialog" data-sentry-source-file="PaymentIssueDownloadDialog.tsx">
      <div className={cn('flex w-full max-w-xl flex-col gap-8', className)} data-testid="payment-issue-download-dialog">
        <div className="flex xs:flex-row flex-col items-center gap-4">
          <div className={imageContainerVariants({
          assetType: asset.type
        })}>
            <Image alt={data.imgAlt} className={imageVariants({
            assetType: asset.type
          })} fill sizes="128px" src={data.imgSrc} data-sentry-element="Image" data-sentry-source-file="PaymentIssueDownloadDialog.tsx" />
            <PremiumStar className={premiumStarVariants({
            isPremiumAsset,
            assetType: asset.type
          })} data-sentry-element="PremiumStar" data-sentry-source-file="PaymentIssueDownloadDialog.tsx" />
          </div>
          <div className={cn('flex w-full flex-col justify-center gap-1 py-0.5', descriptionVariants({
          assetType: asset.type
        }))}>
            <div className="xs:text-left text-center font-heading font-semibold text-xl leading-snug dark:text-white">
              Resolve payment issue <br /> to continue
            </div>
            <div className="xs:text-left text-center text-base text-gray-700 dark:text-gray-200">
              {isPremiumAsset ? "Your payment didn't go through, so your subscription is on hold. Update it to download this premium asset." : "Your payment didn't go through, so your subscription is on hold. Update it to unlock unlimited downloads."}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button asChild className="w-full" fontSize="sm" size="lg" variant="brand" data-sentry-element="Button" data-sentry-source-file="PaymentIssueDownloadDialog.tsx">
            <UpdatePaymentButton data-sentry-element="UpdatePaymentButton" data-sentry-source-file="PaymentIssueDownloadDialog.tsx">Update payment method</UpdatePaymentButton>
          </Button>
          <Button asChild className="w-full hover:bg-transparent dark:hover:bg-transparent" fontSize="sm" noFeedback size="lg" variant="transparent" data-sentry-element="Button" data-sentry-source-file="PaymentIssueDownloadDialog.tsx">
            <div className="text-gray-700">
              Having trouble?{' '}
              <Link className="text-pink-500 underline dark:text-pink-600" href="/help-center/contact-us" target="_blank" data-sentry-element="Link" data-sentry-source-file="PaymentIssueDownloadDialog.tsx">
                Contact support
              </Link>
            </div>
          </Button>
        </div>
      </div>
    </Wrapper>;
};